import moment, { Moment } from 'moment-timezone';

import { NONE_TEXT } from '../../components/displayFields/AxleInfoField';
import { NoWrap } from '../../components/utils/textWrapping';
import { WEEKDAY_FADING_COLOR_PALETTE } from '../../constants/colors';
import { ReturnComponent } from '../reactTypes';
import { DAYS_IN_WEEK } from './constants';
import { guessTimezone } from './misc';

export const withTimezone = (datetime: Moment, timezone: string | null): Moment =>
    datetime.clone().tz(timezone ?? guessTimezone());

export const formatTime = (
    datetime: Moment,
    timezone: string | null,
    { hideTimezone = false, noSpace = false }: { hideTimezone?: boolean; noSpace?: boolean } = {},
): string =>
    withTimezone(datetime, timezone).format(
        'h:mm' + (noSpace ? '' : ' ') + 'a' + (hideTimezone ? '' : ' z'),
    );

export const formatDate = ({
    datetime,
    timezone,
    alwaysShowYear = false,
    showTodayLabel = true,
    formatString,
}: {
    datetime: Moment;
    timezone: string | null;
    alwaysShowYear?: boolean;
    showTodayLabel?: boolean;
    formatString?: string;
}): string => {
    const today = moment();
    datetime = withTimezone(datetime, timezone);
    if (datetime.isSame(today, 'date') && showTodayLabel) {
        return 'Today';
    } else {
        return datetime.format(
            formatString ??
                'ddd MMM D' + (datetime.isSame(today, 'year') && !alwaysShowYear ? '' : ' YYYY'),
        );
    }
};

export const formatNullableDate = ({
    datetime,
    timezone,
    alwaysShowYear = false,
    showDayOfWeek = false,
}: {
    datetime: Moment | null;
    timezone: string | null;
    alwaysShowYear?: boolean;
    showDayOfWeek?: boolean;
}): string => {
    if (datetime === null) return NONE_TEXT;
    const today = moment();
    datetime = withTimezone(datetime, timezone);
    if (datetime.isSame(today, 'date')) {
        return 'Today';
    } else {
        return datetime.format(
            `${showDayOfWeek ? 'ddd ' : ''}MMM D${
                datetime.isSame(today, 'year') && !alwaysShowYear ? '' : ' YYYY'
            }`,
        );
    }
};

export const formatDatetime = (
    datetime: Moment,
    timezone: string | null,
    {
        hideDateWhenToday = false,
        hideTimezone = false,
        alwaysShowYear = false,
    }: { hideDateWhenToday?: boolean; hideTimezone?: boolean; alwaysShowYear?: boolean } = {},
): string => {
    const date = formatDate({ datetime, timezone, alwaysShowYear });
    if (hideDateWhenToday && moment().isSame(datetime, 'date')) {
        return formatTime(datetime, timezone, { hideTimezone });
    } else {
        return date + ', ' + formatTime(datetime, timezone, { hideTimezone });
    }
};

export const formatNullableDatetime = (
    datetime: Moment | null,
    timezone: string | null,
    {
        hideTimezone = false,
        alwaysShowYear = false,
    }: { hideTimezone?: boolean; alwaysShowYear?: boolean } = {},
): string => {
    if (datetime === null) return NONE_TEXT;
    return formatDatetime(datetime, timezone, { hideTimezone, alwaysShowYear });
};

export const formatTimeRange = (
    startDatetime: Moment,
    endDatetime: Moment,
    timezone: string | null,
    options: { showTimezone?: boolean } = { showTimezone: true },
): string => {
    return (
        formatTime(startDatetime, timezone, { hideTimezone: true }) +
        ' – ' +
        formatTime(endDatetime, timezone, { hideTimezone: !options.showTimezone })
    );
};

export const formatDatetimeRange = (
    startDatetime: Moment,
    endDatetime: Moment,
    timezone: string | null,
): string => {
    if (timezone) {
        startDatetime = startDatetime.tz(timezone);
        endDatetime = endDatetime.tz(timezone);
    }
    if (startDatetime.isSame(endDatetime, 'date')) {
        return (
            formatDatetime(startDatetime, timezone, { hideTimezone: true }) +
            ' – ' +
            formatTime(endDatetime, timezone)
        );
    } else {
        return (
            formatDatetime(startDatetime, timezone, { hideTimezone: true }) +
            ' – ' +
            formatDatetime(endDatetime, timezone)
        );
    }
};

export const FormattedDatetime = ({
    datetime,
    timezone,
}: {
    datetime: Moment;
    timezone: string | null;
}): ReturnComponent => {
    return (
        <span>
            <NoWrap>{formatDate({ datetime, timezone, alwaysShowYear: false })}</NoWrap>
            {', '}
            <NoWrap>{formatTime(datetime, timezone)}</NoWrap>
        </span>
    );
};

export const NullableFormattedDatetime = ({
    datetime,
    timezone,
}: {
    datetime: Moment | null;
    timezone: string | null;
}): ReturnComponent => {
    if (datetime === null) return <>{NONE_TEXT}</>;
    return <FormattedDatetime datetime={datetime} timezone={timezone} />;
};

export const NullableFormattedDate = ({
    date,
    alwaysShowYear = false,
}: {
    date: Moment | null;
    alwaysShowYear?: boolean;
}): ReturnComponent => {
    if (date === null) return <>{NONE_TEXT}</>;
    return <NoWrap>{formatDate({ datetime: date, timezone: null, alwaysShowYear })}</NoWrap>;
};

export const formatDuration = (seconds: number | null) =>
    seconds === null ? 'unknown length' : moment.duration(seconds, 'seconds').humanize();

export const formatDayOfWeek = (dayOfWeek: number, short = false): string => {
    switch (dayOfWeek) {
        case 1:
            return short ? 'MO' : 'Monday';
        case 2:
            return short ? 'TU' : 'Tuesday';
        case 3:
            return short ? 'WE' : 'Wednesday';
        case 4:
            return short ? 'TH' : 'Thursday';
        case 5:
            return short ? 'FR' : 'Friday';
        case 6:
            return short ? 'SA' : 'Saturday';
        case 7:
            return short ? 'SU' : 'Sunday';
        default:
            throw new Error(`Invalid day of week: ${dayOfWeek}`);
    }
};

export const colorDateFromDayOfWeekStart = (date: Moment, dayOfWeekStart: number): string => {
    const dayOfWeek = date.isoWeekday();
    const colorIndex = (dayOfWeek - dayOfWeekStart + DAYS_IN_WEEK) % DAYS_IN_WEEK;
    return WEEKDAY_FADING_COLOR_PALETTE[colorIndex];
};

export const getOrdinalForDate = (date: number): string => {
    switch (date) {
        case 1:
        case 21:
        case 31:
            return 'st';
        case 2:
        case 22:
            return 'nd';
        case 3:
        case 23:
            return 'rd';
        default:
            return 'th';
    }
};

export const formatTimeRangeFromStrings = (startTime: string, endTime: string): string => {
    return (
        moment(startTime, 'HH:mm').format('h:mm a') +
        ' – ' +
        moment(endTime, 'HH:mm').format('h:mm a')
    );
};
